import { useCallback } from 'react';
import Grid from '@mui/material/Grid';
import * as Sentry from '@sentry/browser';
import { useMutation } from '@tanstack/react-query';
import useInterview from 'src/interviews/hooks/useInterview';
import useResults from 'src/interviews/hooks/useResults';
import { send, subscribeToMailingList } from 'src/interviews/requests/public_site/emails';
import EmailCollection from 'src/packages/polaris/components/EmailCollection';
import { trackEmailCollectionSubmitted, trackEmailCollectionViewed } from '../../analytics';

import type { MiniInterviewType } from 'src/packages/polaris/types';
import type { EmailCollectionFormData } from 'src/packages/polaris/components/EmailCollection/types';
import type { RichEndingResult } from '../RichEndings/useRichEndingContext';

type Props = {
  descriptionText?: string;
  interviewType: MiniInterviewType;
  result?: RichEndingResult;
  submitText?: string;
};

function EmailTranscriptCard(props: Props) {
  const { descriptionText, interviewType, result, submitText } = props;

  const interview = useInterview();
  const interviewResult = useResults();

  const { mutateAsync: submitEmail } = useMutation({
    mutationFn: async (data: { email: string; referrer?: string; token: string }) =>
      send({
        email: data.email,
        result: {
          interviewResult,
          relatedReadings: result?.relatedReadings,
          treatmentSuppliers: result?.treatmentSuppliers,
        },
        template: interviewType === 'symptoms' ? 'sx' : 'dxoc',
      }),
  });

  const { mutateAsync: subscribeEmail } = useMutation({
    mutationFn: async (data: { email: string; newsletters?: string }) =>
      subscribeToMailingList(data),
  });

  const handleSubmit = useCallback(
    async (data: EmailCollectionFormData) => {
      try {
        await submitEmail({
          email: data.email,
          referrer: window.location.href.split('?')[0],
          token: interview.token || '',
        });

        if (data.newsletterOptIn) {
          await subscribeEmail({
            email: data.email,
            newsletters: data.newsletters.join(','),
          });
        }

        trackEmailCollectionSubmitted({
          descriptionText,
          interviewToken: interview.token || '',
          interviewType,
          submitText,
        });
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    [descriptionText, interview.token, interviewType, submitEmail, submitText, subscribeEmail],
  );

  const handleView = useCallback(() => {
    trackEmailCollectionViewed({
      descriptionText,
      interviewToken: interview.token || '',
      interviewType,
      submitText,
    });
  }, [descriptionText, interview.token, interviewType, submitText]);

  return (
    <Grid item className="bp-w-full">
      <EmailCollection
        {...props}
        onSubmit={handleSubmit}
        onView={handleView}
        submitSuccessMessage="All set! Check your email for a copy of your results."
      />
    </Grid>
  );
}

export default EmailTranscriptCard;
